import React, { useMemo, useState } from 'react';
import { apiUrl, apiUnreachableMessage } from '../config/api';
import { getStoredUser } from '../utils/auth';
import {
  CertificatesNotifyRow,
  EmployeeCertificateFields,
  EmployeeCertificateList,
  uploadClientCertificate,
} from './EmployeeCertificateFields';

function adminHeaders() {
  const user = getStoredUser();
  return { 'X-User-Email': user?.email || '' };
}

export function CertificateUploadModal({ employee, onClose, onSaved }) {
  const existing = useMemo(
    () => (Array.isArray(employee?.certificates) ? employee.certificates : []),
    [employee]
  );
  const [certificates, setCertificates] = useState(existing);
  const [notifyAll, setNotifyAll] = useState(() =>
    existing.length ? existing.every((item) => item.notify !== false) : true
  );
  const [saving, setSaving] = useState(false);
  const [notifyPending, setNotifyPending] = useState(null);
  const [error, setError] = useState('');
  const [createFormOpen, setCreateFormOpen] = useState(false);

  const syncNotifyAll = (list) => {
    setNotifyAll(list.length ? list.every((item) => item.notify !== false) : notifyAll);
  };

  const handleAdd = async (draft) => {
    setSaving(true);
    setError('');
    try {
      const data = await uploadClientCertificate({
        userId: employee.id,
        file: draft.file,
        files: draft.files,
        name: draft.name,
        issuedOn: draft.issued_on,
        expiresOn: draft.expires_on,
        description: draft.description,
        notify: notifyAll,
      });
      const nextList = Array.isArray(data.client?.certificates)
        ? data.client.certificates
        : [...certificates, ...(data.certificates || [])];
      setCertificates(nextList);
      syncNotifyAll(nextList);
      onSaved?.(data.message || 'Certificate added');
    } catch (err) {
      const message = err?.message || apiUnreachableMessage();
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleNotify = async (cert, notify) => {
    setError('');
    setNotifyPending(cert.id);
    const previousList = certificates;
    const previousNotifyAll = notifyAll;
    const nextList = certificates.map((item) =>
      item.id === cert.id ? { ...item, notify } : item
    );
    setCertificates(nextList);
    setNotifyAll(nextList.every((item) => item.notify !== false));
    try {
      const res = await fetch(
        apiUrl(`/api/admin/clients/${employee.id}/certificates/${cert.id}`),
        {
          method: 'PATCH',
          headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ notify }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCertificates(previousList);
        setNotifyAll(previousNotifyAll);
        setError(data.detail || 'Failed to update notification');
        return;
      }
    } catch {
      setCertificates(previousList);
      setNotifyAll(previousNotifyAll);
      setError(apiUnreachableMessage());
    } finally {
      setNotifyPending(null);
    }
  };

  const handleNotifyAll = async (notify) => {
    const previousNotifyAll = notifyAll;
    const previousList = certificates;
    setNotifyAll(notify);
    if (!certificates.length) return;
    setError('');
    setNotifyPending('all');
    setCertificates(certificates.map((item) => ({ ...item, notify })));
    try {
      const res = await fetch(apiUrl(`/api/admin/clients/${employee.id}/certificates`), {
        method: 'PATCH',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notify }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCertificates(previousList);
        setNotifyAll(previousNotifyAll);
        setError(data.detail || 'Failed to update notifications');
        return;
      }
      const nextList = Array.isArray(data.certificates)
        ? data.certificates
        : previousList.map((item) => ({ ...item, notify }));
      setCertificates(nextList);
      setNotifyAll(nextList.every((item) => item.notify !== false));
    } catch {
      setCertificates(previousList);
      setNotifyAll(previousNotifyAll);
      setError(apiUnreachableMessage());
    } finally {
      setNotifyPending(null);
    }
  };

  const handleRemove = async (cert) => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(
        apiUrl(`/api/admin/clients/${employee.id}/certificates/${cert.id}`),
        { method: 'DELETE', headers: adminHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to remove certificate');
        return;
      }
      setCertificates((prev) => {
        const nextList = prev.filter((item) => item.id !== cert.id);
        syncNotifyAll(nextList);
        return nextList;
      });
      onSaved?.(data.message || 'Certificate removed');
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 p-2 sm:items-center sm:p-3"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[calc(100dvh-0.5rem)] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl border border-gray-700 bg-gray-950 pt-3 pl-3 pb-3 shadow-xl sm:min-h-[22rem] sm:max-h-[90vh] sm:rounded-lg sm:pt-5 sm:pl-5 sm:pb-5"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Certificates"
      >
        <div className="mb-3 flex shrink-0 items-start justify-between gap-2 pr-3 sm:pr-5">
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-white">Certificates</h4>
            <p className="mt-0.5 truncate text-[11px] text-gray-400">
              {employee?.name || employee?.project_name || employee?.email}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xl leading-none text-gray-400 hover:bg-white/10 hover:text-white"
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
        <div className="mb-3 shrink-0 pr-3 sm:pr-5">
          <CertificatesNotifyRow
            on={notifyAll}
            onToggle={handleNotifyAll}
            disabled={notifyPending === 'all'}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1.5 sm:pr-2">
          <div className="pr-1.5 sm:pr-3">
            <div className="rounded-lg border border-gray-800 bg-black/20">
              <button
                type="button"
                onClick={() => setCreateFormOpen((open) => !open)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-white/[0.03]"
                aria-expanded={createFormOpen}
                aria-controls="certificate-create-form"
              >
                <span className="text-sm font-bold text-white sm:text-base">
                  Certificate create form
                </span>
                <span
                  className={`inline-flex h-7 w-7 shrink-0 items-center justify-center text-lg leading-none text-gray-300 transition-transform sm:text-xl ${
                    createFormOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                >
                  ▾
                </span>
              </button>
              {createFormOpen ? (
                <div id="certificate-create-form" className="border-t border-gray-800 px-3 pb-3 pt-3">
                  <EmployeeCertificateFields
                    onAdd={handleAdd}
                    saving={saving}
                    compact
                    showAddButton
                    notifyOn={notifyAll}
                  />
                  {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
                </div>
              ) : null}
            </div>
            <div className="mt-4 border-t border-gray-800 pt-3">
              <EmployeeCertificateList
                certificates={certificates}
                onRemove={handleRemove}
                onNotify={handleNotify}
                saving={saving}
                notifyPendingId={notifyPending}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CertificateUploadModal;
